// app/(routes)/tools/ai-for-financial-advisors/WorkflowFinder.tsx
"use client";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { HTFButton } from "@/app/shared/components/button/HTFButtonComponent";
import { useClientReady } from "@/app/shared/hooks/useClientReady";
import { cn } from "@/lib/utils";

import { DarkGridBackdrop } from "./components/report-ui";
import { WorkflowReport } from "./components/WorkflowReport";
import {
  FIRM_LOOKUP_DEBOUNCE_MS,
  firmLookupQueryKey,
  isFirmLookupEnabled,
  normalizeFirmLookupQuery,
} from "./firmLookupQueryPolicy";
import {
  getBrowserSessionId,
  getStoredId,
  WORKFLOW_FINDER_API as API,
} from "./workflow-finder.analytics";
import type {
  AdvisorFirm,
  AumRange,
  OrganizationType,
  PrimaryGoal,
  SystemCode,
  WorkflowFrequency,
  WorkflowNeed,
  WorkflowRecommendation,
} from "./workflow-finder.types";

const GOALS: Array<{
  value: PrimaryGoal;
  label: string;
  detail: string;
}> = [
  {
    value: "advisor_capacity",
    label: "Give advisors more capacity",
    detail: "Reduce preparation, follow-up, and internal search work.",
  },
  {
    value: "client_onboarding",
    label: "Onboard clients faster",
    detail: "Reduce missing items, manual chasing, and status ambiguity.",
  },
  {
    value: "advisor_onboarding",
    label: "Recruit and onboard advisors",
    detail: "Coordinate intake, registration, systems, billing, and readiness.",
  },
  {
    value: "client_service",
    label: "Improve ongoing client service",
    detail: "Route requests and prepare proactive, reviewable client work.",
  },
  {
    value: "compliance",
    label: "Strengthen compliance and supervision",
    detail:
      "Prepare evidence, flag exceptions, and keep CCO judgment in control.",
  },
  {
    value: "billing_fees",
    label: "Reduce billing and fee errors",
    detail: "Audit fees and resolve billing or payout exceptions earlier.",
  },
  {
    value: "reporting_data",
    label: "Improve reporting and data access",
    detail: "Make client and advisor information easier to find and review.",
  },
  {
    value: "growth",
    label: "Grow the firm",
    detail: "Improve prospect research, qualification, and follow-through.",
  },
  {
    value: "not_sure",
    label: "I am not sure yet",
    detail: "Show me a practical, low-risk place to start.",
  },
];

const WORKFLOWS: Record<
  WorkflowNeed,
  { label: string; detail: string; goal: PrimaryGoal }
> = {
  meeting_crm: {
    label: "Meeting intelligence and CRM follow-through",
    detail: "Meeting brief, proposed CRM fields, tasks, and follow-up draft.",
    goal: "advisor_capacity",
  },
  unified_client_intelligence: {
    label: "Unified client intelligence brief",
    detail:
      "Current client facts, open work, recent activity, and source links.",
    goal: "advisor_capacity",
  },
  internal_knowledge: {
    label: "Institutional knowledge with cited sources",
    detail:
      "Find current procedures and research with a clear escalation path.",
    goal: "advisor_capacity",
  },
  client_onboarding: {
    label: "Express client onboarding",
    detail: "Missing items, owners, blockers, and the next safe action.",
    goal: "client_onboarding",
  },
  client_communications: {
    label: "Grounded client communication drafts",
    detail: "Source-linked drafts with missing-information flags and approval.",
    goal: "client_onboarding",
  },
  advisor_onboarding: {
    label: "Advisor acquisition and onboarding",
    detail:
      "Milestones across recruiting, compliance, IT, billing, and operations.",
    goal: "advisor_onboarding",
  },
  advisor_roster_intake: {
    label: "Standardized incoming book intake",
    detail:
      "Normalize rosters and surface account, household, beneficiary, and fee exceptions.",
    goal: "advisor_onboarding",
  },
  service_request_triage: {
    label: "Client service request triage",
    detail: "Classify, assemble context, route, and prepare a response draft.",
    goal: "client_service",
  },
  proactive_client_service: {
    label: "Proactive client service moments",
    detail: "A sourced review queue for clients who may need attention.",
    goal: "client_service",
  },
  compliance_marketing_review: {
    label: "AI-assisted marketing review",
    detail:
      "Flag language, gather evidence, and check disclosures before CCO review.",
    goal: "compliance",
  },
  compliance_exam_records: {
    label: "Books, records, and exam-response preparation",
    detail: "Map requests to retained evidence and surface missing artifacts.",
    goal: "compliance",
  },
  compliance_answer_desk: {
    label: "Compliance question triage",
    detail: "Retrieve cited policy, prepare a draft, or escalate uncertainty.",
    goal: "compliance",
  },
  billing_fee_audit: {
    label: "Automated fee audit",
    detail:
      "Compare billed fees with contracts, schedules, and approved exceptions.",
    goal: "billing_fees",
  },
  billing_exception_triage: {
    label: "Billing and payout exception triage",
    detail:
      "Route exceptions with the evidence needed for an approved resolution.",
    goal: "billing_fees",
  },
  client_reporting: {
    label: "Review-ready client reporting",
    detail:
      "Assemble performance, planning, service, and tax context into a draft.",
    goal: "reporting_data",
  },
  prospect_intelligence: {
    label: "AI prospect intelligence",
    detail: "A sourced research and qualification queue before outreach.",
    goal: "growth",
  },
};

const WORKFLOWS_BY_GOAL: Record<PrimaryGoal, WorkflowNeed[]> = {
  advisor_capacity: [
    "meeting_crm",
    "unified_client_intelligence",
    "internal_knowledge",
  ],
  client_onboarding: ["client_onboarding", "client_communications"],
  advisor_onboarding: ["advisor_onboarding", "advisor_roster_intake"],
  client_service: [
    "service_request_triage",
    "proactive_client_service",
    "meeting_crm",
  ],
  compliance: [
    "compliance_marketing_review",
    "compliance_exam_records",
    "compliance_answer_desk",
  ],
  billing_fees: ["billing_fee_audit", "billing_exception_triage"],
  reporting_data: ["client_reporting", "unified_client_intelligence"],
  growth: ["prospect_intelligence"],
  not_sure: ["meeting_crm", "client_onboarding", "compliance_answer_desk"],
};

const FREQUENCIES: Array<{
  value: WorkflowFrequency;
  label: string;
}> = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "ad_hoc", label: "As needed" },
];

const AUM_RANGES: Array<{ value: AumRange; label: string }> = [
  { value: "under_100m", label: "Under $100M" },
  { value: "100m_250m", label: "$100M to $250M" },
  { value: "250m_500m", label: "$250M to $500M" },
  { value: "500m_1b", label: "$500M to $1B" },
  { value: "1b_5b", label: "$1B to $5B" },
  { value: "5b_plus", label: "$5B or more" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const ORGANIZATION_TYPES: Array<{
  value: OrganizationType;
  label: string;
  detail: string;
}> = [
  {
    value: "wealth_management",
    label: "Wealth management or RIA",
    detail: "Advisory, planning, broker-dealer, or wealth operations.",
  },
  {
    value: "other_financial_services",
    label: "Other financial services",
    detail: "Banking, insurance, fintech, lending, accounting, or adjacent.",
  },
  {
    value: "other_industry",
    label: "Outside financial services",
    detail: "We will use a general customer and operations vocabulary.",
  },
];

const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Brazil",
  "Germany",
  "France",
  "Singapore",
  "Switzerland",
];

const SYSTEMS: Array<{
  value: SystemCode;
  label: string;
  group: string;
  goals?: PrimaryGoal[];
}> = [
  { value: "redtail", label: "Redtail CRM", group: "CRM" },
  { value: "wealthbox", label: "Wealthbox CRM", group: "CRM" },
  { value: "salesforce", label: "Salesforce", group: "CRM" },
  {
    value: "orion_tamarac",
    label: "Orion or Tamarac",
    group: "Portfolio, reporting, and billing",
    goals: ["billing_fees", "reporting_data", "advisor_capacity"],
  },
  {
    value: "black_diamond_addepar",
    label: "Black Diamond or Addepar",
    group: "Portfolio, reporting, and billing",
    goals: ["billing_fees", "reporting_data", "advisor_capacity"],
  },
  {
    value: "billing_system",
    label: "Another billing or fee system",
    group: "Portfolio, reporting, and billing",
    goals: ["billing_fees"],
  },
  {
    value: "precisefp",
    label: "PreciseFP",
    group: "Planning and onboarding",
    goals: ["client_onboarding", "advisor_onboarding"],
  },
  {
    value: "planning_software",
    label: "eMoney, MoneyGuidePro, or RightCapital",
    group: "Planning and onboarding",
    goals: ["client_onboarding", "client_service", "reporting_data"],
  },
  {
    value: "schwab_fidelity_pershing",
    label: "Schwab, Fidelity, or Pershing",
    group: "Custody",
    goals: [
      "client_onboarding",
      "advisor_onboarding",
      "billing_fees",
      "reporting_data",
    ],
  },
  {
    value: "altruist",
    label: "Altruist",
    group: "Custody",
    goals: ["client_onboarding", "billing_fees", "reporting_data"],
  },
  {
    value: "compliance_archive",
    label: "Compliance or communications archive",
    group: "Compliance",
    goals: ["compliance", "client_service"],
  },
  {
    value: "microsoft_365",
    label: "Microsoft 365",
    group: "Work and communication",
  },
  {
    value: "google_workspace",
    label: "Google Workspace",
    group: "Work and communication",
  },
  {
    value: "slack_teams",
    label: "Slack or Microsoft Teams",
    group: "Work and communication",
  },
  { value: "other", label: "Another system", group: "Other" },
  { value: "not_sure", label: "Not sure yet", group: "Other" },
];

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API}/${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  const data = (await response.json()) as T & {
    error?: string;
    detail?: string | Array<{ msg?: string }>;
  };
  if (!response.ok) {
    const detail = Array.isArray(data.detail)
      ? data.detail
          .map((item) => item.msg)
          .filter(Boolean)
          .join(", ")
      : data.detail;
    throw new Error(detail || data.error || "Something went wrong");
  }
  return data;
}

function getRunId(): string {
  const key = "htf_workflow_finder_run_v2";
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const value = crypto.randomUUID();
  window.sessionStorage.setItem(key, value);
  return value;
}

function resetRunId() {
  window.sessionStorage.removeItem("htf_workflow_finder_run_v2");
}

function scrollToTool(id: string) {
  const useInstantScroll =
    window.innerWidth < 640 ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(id)?.scrollIntoView({
    behavior: useInstantScroll ? "auto" : "smooth",
    block: "start",
  });
}

function aumRangeFor(value?: number): AumRange | "" {
  if (!value) return "";
  if (value < 100_000_000) return "under_100m";
  if (value < 250_000_000) return "100m_250m";
  if (value < 500_000_000) return "250m_500m";
  if (value < 1_000_000_000) return "500m_1b";
  if (value < 5_000_000_000) return "1b_5b";
  return "5b_plus";
}

function formatAum(value?: number): string | null {
  if (!value) return null;
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B AUM`;
  }
  if (value >= 1_000_000) return `$${Math.round(value / 1_000_000)}M AUM`;
  return null;
}

function workflowOptionsFor(goals: PrimaryGoal[]): WorkflowNeed[] {
  if (goals.length === 0) return [];
  if (goals.includes("not_sure")) return WORKFLOWS_BY_GOAL.not_sure;

  const options: WorkflowNeed[] = [];
  const longest = Math.max(
    ...goals.map((goal) => WORKFLOWS_BY_GOAL[goal].length),
  );
  for (let index = 0; index < longest && options.length < 8; index += 1) {
    for (const goal of goals) {
      const option = WORKFLOWS_BY_GOAL[goal][index];
      if (option && !options.includes(option)) options.push(option);
      if (options.length === 8) break;
    }
  }
  return options;
}

function Choice({
  selected,
  label,
  detail,
  onClick,
  testId,
}: {
  selected: boolean;
  label: string;
  detail?: string;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "group flex min-h-20 w-full items-start gap-3 rounded-2xl bg-layer-1 px-4 py-4 text-left shadow-sm ring-1 ring-border/70 transition duration-200 active:scale-[0.98]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected
          ? "bg-secondary/10 ring-2 ring-secondary"
          : "hover:-translate-y-0.5 hover:shadow-md",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
          selected
            ? "bg-secondary text-on-dark"
            : "bg-surface text-transparent ring-1 ring-border",
        )}
      >
        <Check size={14} aria-hidden="true" strokeWidth={3} />
      </span>
      <span>
        <span className="block font-heading text-base font-bold leading-snug text-text">
          {label}
        </span>
        {detail && (
          <span className="mt-1 block text-sm leading-snug text-text-secondary">
            {detail}
          </span>
        )}
      </span>
    </button>
  );
}

function FirmLookup({
  firm,
  manualName,
  organizationType,
  country,
  aumRange,
  teamSize,
  correcting,
  onFirm,
  onManualName,
  onOrganizationType,
  onCountry,
  onAumRange,
  onTeamSize,
  onToggleCorrecting,
}: {
  firm: AdvisorFirm | null;
  manualName: string;
  organizationType: OrganizationType | "";
  country: string;
  aumRange: AumRange | "";
  teamSize: string;
  correcting: boolean;
  onFirm: (firm: AdvisorFirm | null) => void;
  onManualName: (name: string) => void;
  onOrganizationType: (value: OrganizationType | "") => void;
  onCountry: (value: string) => void;
  onAumRange: (range: AumRange | "") => void;
  onTeamSize: (count: string) => void;
  onToggleCorrecting: (value: boolean) => void;
}) {
  const [query, setQuery] = useState(manualName);
  const [debouncedQuery, setDebouncedQuery] = useState(() =>
    normalizeFirmLookupQuery(manualName),
  );
  const [usingManualProfile, setUsingManualProfile] = useState(
    Boolean(manualName && !firm),
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(normalizeFirmLookupQuery(query));
    }, FIRM_LOOKUP_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  const firmsEnabled = isFirmLookupEnabled({
    hasSelectedFirm: Boolean(firm),
    usingManualProfile,
    normalizedQuery: debouncedQuery,
  });
  const firmsQuery = useQuery({
    queryKey: firmLookupQueryKey(debouncedQuery),
    queryFn: ({ signal }) =>
      api<{ firms: AdvisorFirm[] }>(
        `firms?q=${encodeURIComponent(debouncedQuery)}`,
        { signal },
      ),
    enabled: firmsEnabled,
  });
  const results = firmsEnabled ? (firmsQuery.data?.firms ?? []) : [];
  const loading = firmsEnabled && firmsQuery.isFetching;
  const searchedQuery = firmsEnabled ? debouncedQuery : "";

  // Selecting a Form ADV match means we already know the real numbers.
  // We never bucket them into a range here — only an explicit correction does that.
  const selectFirm = (result: AdvisorFirm) => {
    onFirm(result);
    onManualName("");
    setQuery(result.company_name);
    setUsingManualProfile(false);
    onToggleCorrecting(false);
    onAumRange("");
    onTeamSize("");
    onOrganizationType("wealth_management");
    onCountry("United States");
  };

  const clearFirm = () => {
    onFirm(null);
    onManualName("");
    onAumRange("");
    onTeamSize("");
    onOrganizationType("");
    onCountry("");
    onToggleCorrecting(false);
    setUsingManualProfile(false);
    setQuery("");
  };

  const useManualProfile = () => {
    onFirm(null);
    onManualName(query.trim());
    onToggleCorrecting(false);
    setUsingManualProfile(true);
  };

  const startCorrecting = () => {
    onAumRange(aumRangeFor(firm?.regulatory_aum) || "");
    onTeamSize(firm?.advisory_employees ? String(firm.advisory_employees) : "");
    onToggleCorrecting(true);
  };

  const cancelCorrecting = () => {
    onAumRange("");
    onTeamSize("");
    onToggleCorrecting(false);
  };

  const facts = firm
    ? [
        formatAum(firm.regulatory_aum),
        firm.advisory_employees
          ? `${firm.advisory_employees} advisory employees`
          : null,
        firm.state,
        firm.crd_number ? `CRD ${firm.crd_number}` : null,
      ].filter(Boolean)
    : [];

  return (
    <div className="relative">
      <label
        htmlFor="firm-search"
        className="mb-2 block font-heading text-xs font-black uppercase tracking-wider text-text-secondary"
      >
        Firm or company name
      </label>
      <div className="relative">
        <Search
          aria-hidden="true"
          size={20}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary"
        />
        <input
          id="firm-search"
          data-testid="advisor-workflow-finder-search"
          value={query}
          onChange={(event) => {
            const value = event.target.value;
            setQuery(value);
            setUsingManualProfile(false);
            onFirm(null);
            onManualName(value);
            onOrganizationType("");
            onCountry("");
            onAumRange("");
            onTeamSize("");
          }}
          placeholder="Type your company name"
          autoComplete="organization"
          className="h-14 w-full rounded-xl bg-layer-1 pl-12 pr-4 text-base text-text shadow-sm outline-none ring-1 ring-border transition focus:ring-2 focus:ring-ring"
        />
      </div>

      {!firm && !usingManualProfile && (results.length > 0 || loading) && (
        <div className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-xl bg-layer-1 p-1 shadow-xl ring-1 ring-border">
          {loading && (
            <p className="px-4 py-3 text-sm text-text-secondary">Searching…</p>
          )}
          {!loading &&
            results.map((result) => (
              <button
                key={result.company_id}
                type="button"
                data-testid="advisor-workflow-finder-firm-result"
                onClick={() => selectFirm(result)}
                className="block w-full rounded-lg px-4 py-3 text-left transition hover:bg-surface"
              >
                <span className="block font-heading text-sm font-bold text-text">
                  {result.company_name}
                </span>
                <span className="mt-0.5 block text-xs text-text-secondary">
                  {[result.state, formatAum(result.regulatory_aum)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            ))}
          {!loading && query.trim().length >= 2 && (
            <button
              type="button"
              onClick={useManualProfile}
              className="mt-1 flex min-h-12 w-full items-center justify-between rounded-lg bg-surface px-4 py-3 text-left text-sm font-bold text-link"
            >
              Use “{query.trim()}” without a public match
              <ArrowRight size={15} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {firm && (
        <div
          data-testid="advisor-workflow-finder-snapshot"
          className="mt-3 rounded-xl bg-secondary/10 px-4 py-4"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-secondary-dark">
                <CheckCircle2 size={13} aria-hidden="true" />
                Matched to public Form ADV data
              </p>
              <p className="mt-1.5 font-heading text-base font-bold text-text">
                {firm.company_name}
              </p>
            </div>
            <button
              type="button"
              onClick={clearFirm}
              className="min-h-10 shrink-0 rounded-lg px-3 text-xs font-semibold text-link hover:bg-surface"
            >
              Change
            </button>
          </div>

          {facts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {facts.map((fact) => (
                <span
                  key={fact}
                  className="rounded-full bg-card px-3 py-1 text-xs font-semibold text-text"
                >
                  {fact}
                </span>
              ))}
            </div>
          )}

          <p className="mt-3 text-xs leading-relaxed text-text-tertiary">
            We use these public numbers as your starting profile.{" "}
            {!correcting && (
              <button
                type="button"
                onClick={startCorrecting}
                className="font-semibold text-link underline underline-offset-2"
              >
                Not accurate? Correct it.
              </button>
            )}
          </p>

          {correcting && (
            <div className="mt-4 grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="aum-range"
                  className="mb-2 block font-heading text-xs font-black uppercase tracking-wider text-text-secondary"
                >
                  Corrected approximate AUM
                </label>
                <select
                  id="aum-range"
                  value={aumRange}
                  onChange={(event) =>
                    onAumRange(event.target.value as AumRange | "")
                  }
                  className="h-12 w-full rounded-xl bg-layer-1 px-4 text-sm text-text shadow-sm outline-none ring-1 ring-border focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select a range</option>
                  {AUM_RANGES.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="advisor-count"
                  className="mb-2 block font-heading text-xs font-black uppercase tracking-wider text-text-secondary"
                >
                  Corrected team or advisor count
                </label>
                <input
                  id="advisor-count"
                  type="number"
                  min="1"
                  max="100000"
                  inputMode="numeric"
                  value={teamSize}
                  onChange={(event) => onTeamSize(event.target.value)}
                  placeholder="For example, 12"
                  className="h-12 w-full rounded-xl bg-layer-1 px-4 text-sm text-text shadow-sm outline-none ring-1 ring-border focus:ring-2 focus:ring-ring"
                />
              </div>
              <button
                type="button"
                onClick={cancelCorrecting}
                className="min-h-9 text-xs font-semibold text-text-secondary underline underline-offset-2 sm:col-span-2 sm:justify-self-start"
              >
                Use the public numbers instead
              </button>
            </div>
          )}
        </div>
      )}

      {!firm &&
        !usingManualProfile &&
        !loading &&
        query.trim().length >= 2 &&
        searchedQuery === query.trim() &&
        results.length === 0 && (
          <button
            type="button"
            onClick={useManualProfile}
            className="mt-3 flex min-h-14 w-full items-center justify-between rounded-xl bg-primary/10 px-4 py-3 text-left text-sm font-bold text-text ring-1 ring-primary/20"
          >
            <span>
              No public Form ADV match. Create a profile for “{query.trim()}”
            </span>
            <ArrowRight size={16} className="shrink-0 text-link" />
          </button>
        )}

      {!firm && usingManualProfile && (
        <div className="mt-4 space-y-6 border-t border-border pt-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-secondary-dark">
                Company profile
              </p>
              <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                No CRD or Form ADV number needed. These details change the
                recommendation and stay labeled as information you provided.
              </p>
            </div>
            <button
              type="button"
              onClick={clearFirm}
              className="min-h-10 shrink-0 rounded-lg px-3 text-xs font-semibold text-link hover:bg-surface"
            >
              Change
            </button>
          </div>

          <fieldset>
            <legend className="mb-3 text-xs font-bold uppercase tracking-wider text-text-secondary">
              What kind of company is it?
            </legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {ORGANIZATION_TYPES.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={organizationType === option.value}
                  onClick={() => {
                    onOrganizationType(option.value);
                    onAumRange(
                      option.value === "other_industry"
                        ? "not_applicable"
                        : aumRange === "not_applicable"
                          ? ""
                          : aumRange,
                    );
                  }}
                  className={cn(
                    "min-h-24 rounded-xl bg-layer-1 px-4 py-3 text-left ring-1 ring-border transition",
                    organizationType === option.value &&
                      "bg-secondary/10 ring-2 ring-secondary",
                  )}
                >
                  <span className="block font-heading text-sm font-bold text-text">
                    {option.label}
                  </span>
                  <span className="mt-1 block text-xs leading-snug text-text-secondary">
                    {option.detail}
                  </span>
                </button>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="company-country"
                className="mb-2 block font-heading text-xs font-black uppercase tracking-wider text-text-secondary"
              >
                Country
              </label>
              <input
                id="company-country"
                list="workflow-finder-countries"
                value={country}
                onChange={(event) => onCountry(event.target.value)}
                placeholder="Start typing a country"
                autoComplete="country-name"
                className="h-12 w-full rounded-xl bg-layer-1 px-4 text-sm text-text outline-none ring-1 ring-border focus:ring-2 focus:ring-ring"
              />
              <datalist id="workflow-finder-countries">
                {COUNTRIES.map((item) => (
                  <option key={item} value={item} />
                ))}
              </datalist>
            </div>

            {organizationType !== "other_industry" && (
              <div>
                <label
                  htmlFor="aum-range"
                  className="mb-2 block font-heading text-xs font-black uppercase tracking-wider text-text-secondary"
                >
                  Approximate AUM
                </label>
                <select
                  id="aum-range"
                  value={aumRange}
                  onChange={(event) =>
                    onAumRange(event.target.value as AumRange | "")
                  }
                  className="h-12 w-full rounded-xl bg-layer-1 px-4 text-sm text-text outline-none ring-1 ring-border focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select a range</option>
                  {AUM_RANGES.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label
                htmlFor="team-size"
                className="mb-2 block font-heading text-xs font-black uppercase tracking-wider text-text-secondary"
              >
                Approximate team size{" "}
                <span className="normal-case">(optional)</span>
              </label>
              <input
                id="team-size"
                type="number"
                min="1"
                max="1000000"
                inputMode="numeric"
                value={teamSize}
                onChange={(event) => onTeamSize(event.target.value)}
                placeholder="For example, 25"
                className="h-12 w-full rounded-xl bg-layer-1 px-4 text-sm text-text outline-none ring-1 ring-border focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TOTAL_VISUAL_STEPS = 4;

interface WorkflowFinderProps {
  initialResult?: WorkflowRecommendation;
}

export function WorkflowFinder({ initialResult }: WorkflowFinderProps = {}) {
  const ready = useClientReady();
  const [step, setStep] = useState(1);
  const [firmConfirmed, setFirmConfirmed] = useState(false);
  const [firm, setFirm] = useState<AdvisorFirm | null>(null);
  const [manualName, setManualName] = useState("");
  const [organizationType, setOrganizationType] = useState<
    OrganizationType | ""
  >("");
  const [country, setCountry] = useState("");
  const [aumRange, setAumRange] = useState<AumRange | "">("");
  const [teamSize, setTeamSize] = useState("");
  const [correctingNumbers, setCorrectingNumbers] = useState(false);
  const [goals, setGoals] = useState<PrimaryGoal[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowNeed[]>([]);
  const [frequencies, setFrequencies] = useState<
    Partial<Record<WorkflowNeed, WorkflowFrequency>>
  >({});
  const [systems, setSystems] = useState<SystemCode[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [result, setResult] = useState<WorkflowRecommendation | null>(
    initialResult ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const visibleWorkflows = useMemo(() => workflowOptionsFor(goals), [goals]);
  const visibleGoals = useMemo(
    () =>
      organizationType === "other_industry"
        ? GOALS.filter((goal) =>
            [
              "advisor_capacity",
              "client_onboarding",
              "client_service",
              "reporting_data",
              "growth",
              "not_sure",
            ].includes(goal.value),
          )
        : GOALS,
    [organizationType],
  );
  const visibleSystems = useMemo(
    () =>
      SYSTEMS.filter(
        (system) =>
          (organizationType !== "other_industry" ||
            ["CRM", "Work and communication", "Other"].includes(
              system.group,
            )) &&
          (!system.goals ||
            system.goals.some((goal) => goals.includes(goal)) ||
            goals.includes("not_sure")),
      ),
    [goals, organizationType],
  );
  const groupedSystems = useMemo(() => {
    const groups = new Map<string, typeof visibleSystems>();
    for (const system of visibleSystems) {
      const current = groups.get(system.group) || [];
      groups.set(system.group, [...current, system]);
    }
    return [...groups.entries()];
  }, [visibleSystems]);

  const firmName =
    firm?.company_name ||
    manualName.trim() ||
    result?.company_profile.name ||
    "Your firm";
  const teamNumber = Number(teamSize);
  const companyReady = Boolean(firm) || manualName.trim().length >= 2;
  const manualProfileReady = firm
    ? true
    : Boolean(organizationType) &&
      country.trim().length >= 2 &&
      (organizationType === "other_industry" || Boolean(aumRange)) &&
      (teamSize === "" || (Number.isInteger(teamNumber) && teamNumber > 0));
  const canContinue =
    (step === 1 && !firmConfirmed && companyReady && manualProfileReady) ||
    (step === 1 && firmConfirmed && goals.length > 0) ||
    (step === 2 &&
      workflows.length > 0 &&
      workflows.every((workflow) => Boolean(frequencies[workflow]))) ||
    (step === 3 && systems.length > 0);
  const visualStep = !firmConfirmed ? 1 : step + 1;
  const progress = `${Math.round((visualStep / TOTAL_VISUAL_STEPS) * 100)}%`;

  function toggleGoal(value: PrimaryGoal) {
    setGoals((current) => {
      const next =
        value === "not_sure"
          ? current.includes(value)
            ? []
            : [value]
          : current.filter((goal) => goal !== "not_sure").includes(value)
            ? current.filter((goal) => goal !== value && goal !== "not_sure")
            : [...current.filter((goal) => goal !== "not_sure"), value];
      const allowed = new Set(workflowOptionsFor(next));
      setWorkflows((selected) =>
        selected.filter((workflow) => allowed.has(workflow)),
      );
      setFrequencies(
        (selected) =>
          Object.fromEntries(
            Object.entries(selected).filter(([workflow]) =>
              allowed.has(workflow as WorkflowNeed),
            ),
          ) as Partial<Record<WorkflowNeed, WorkflowFrequency>>,
      );
      return next;
    });
  }

  function toggleWorkflow(value: WorkflowNeed) {
    setWorkflows((current) => {
      if (current.includes(value)) {
        setFrequencies((selected) => {
          const next = { ...selected };
          delete next[value];
          return next;
        });
        return current.filter((item) => item !== value);
      }
      setFrequencies((selected) => ({ ...selected, [value]: "weekly" }));
      return [...current, value];
    });
  }

  function toggleSystem(value: SystemCode) {
    setSystems((current) => {
      if (value === "not_sure") return current.includes(value) ? [] : [value];
      const withoutUnsure = current.filter((item) => item !== "not_sure");
      return withoutUnsure.includes(value)
        ? withoutUnsure.filter((item) => item !== value)
        : [...withoutUnsure, value];
    });
  }

  async function ensureSession(): Promise<{ id: string; token: string }> {
    if (sessionId && sessionToken) {
      return { id: sessionId, token: sessionToken };
    }
    const params = new URLSearchParams(window.location.search);
    const attribution = Object.fromEntries(
      [...params.entries()].filter(
        ([key]) =>
          key.startsWith("utm_") || ["gclid", "ref", "source"].includes(key),
      ),
    );
    const data = await api<{
      session: { id: string; session_token: string };
    }>("sessions", {
      method: "POST",
      body: JSON.stringify({
        idempotency_key: getRunId(),
        anonymous_id: getStoredId("htf_anon_id"),
        browser_session_id: getBrowserSessionId(),
        company_id: firm?.company_id,
        company_name: firm ? undefined : manualName.trim(),
        organization_type:
          organizationType || (firm ? "wealth_management" : undefined),
        country: country.trim() || (firm ? "United States" : undefined),
        reported_aum_range: aumRange || undefined,
        reported_advisor_count:
          organizationType === "wealth_management" && teamNumber > 0
            ? teamNumber
            : undefined,
        reported_team_size: teamNumber > 0 ? teamNumber : undefined,
        source_article_id: params.get("article_id") || undefined,
        source_article_slug: params.get("article_slug") || undefined,
        attribution,
      }),
    });
    setSessionId(data.session.id);
    setSessionToken(data.session.session_token);
    return { id: data.session.id, token: data.session.session_token };
  }

  async function continueStep() {
    if (!canContinue || loading) return;
    if (step === 1 && !firmConfirmed) {
      setFirmConfirmed(true);
      window.setTimeout(() => scrollToTool("workflow-finder"), 50);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const session = await ensureSession();
      if (step < 3) {
        await api(`sessions/${session.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            session_token: session.token,
            current_step: step,
            goals,
            workflow_needs: workflows.length ? workflows : undefined,
            workflow_frequencies:
              workflows.length > 0 ? frequencies : undefined,
            systems: systems.length ? systems : undefined,
            organization_type:
              organizationType || (firm ? "wealth_management" : undefined),
            country: country.trim() || (firm ? "United States" : undefined),
            reported_aum_range: aumRange || undefined,
            reported_advisor_count:
              organizationType === "wealth_management" && teamNumber > 0
                ? teamNumber
                : undefined,
            reported_team_size: teamNumber > 0 ? teamNumber : undefined,
          }),
        });
        setStep((current) => current + 1);
        window.setTimeout(() => scrollToTool("workflow-finder"), 50);
      } else {
        const data = await api<{ recommendation: WorkflowRecommendation }>(
          `sessions/${session.id}/recommendation`,
          {
            method: "POST",
            body: JSON.stringify({
              session_token: session.token,
              goals,
              workflow_needs: workflows,
              workflow_frequencies: frequencies,
              systems,
              organization_type:
                organizationType || (firm ? "wealth_management" : undefined),
              country: country.trim() || (firm ? "United States" : undefined),
              reported_aum_range: aumRange || undefined,
              reported_advisor_count:
                organizationType === "wealth_management" && teamNumber > 0
                  ? teamNumber
                  : undefined,
              reported_team_size: teamNumber > 0 ? teamNumber : undefined,
            }),
          },
        );
        setResult(data.recommendation);
        window.setTimeout(() => scrollToTool("workflow-result"), 50);
      }
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  }

  async function requestEmail(
    submittedName: string,
    submittedEmail: string,
  ): Promise<string> {
    if (!sessionId || !sessionToken) {
      return "Could not send the email";
    }
    try {
      const data = await api<{ email_delivery_status: string }>(
        `sessions/${sessionId}/identify`,
        {
          method: "POST",
          body: JSON.stringify({
            session_token: sessionToken,
            name: submittedName,
            work_email: submittedEmail,
            marketing_consent: false,
          }),
        },
      );
      return data.email_delivery_status === "sent"
        ? "Sent. Check your inbox."
        : "Saved. Your full opportunity map remains on this page.";
    } catch (caught) {
      return caught instanceof Error
        ? caught.message
        : "Could not send the email";
    }
  }

  function trackHighIntent(
    intent: "contact" | "solution" | "proposal",
    target: string,
  ) {
    if (!sessionId || !sessionToken) return;
    void fetch(`${API}/sessions/${sessionId}/high-intent`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ session_token: sessionToken, intent, target }),
      keepalive: true,
    });
  }

  function goBack() {
    if (step === 1 && firmConfirmed) {
      setFirmConfirmed(false);
      return;
    }
    setStep((current) => current - 1);
  }

  function startOver() {
    resetRunId();
    setResult(null);
    setStep(1);
    setFirmConfirmed(false);
    setFirm(null);
    setManualName("");
    setOrganizationType("");
    setCountry("");
    setAumRange("");
    setTeamSize("");
    setCorrectingNumbers(false);
    setGoals([]);
    setWorkflows([]);
    setFrequencies({});
    setSystems([]);
    setSessionId(null);
    setSessionToken(null);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (result) {
    return (
      <WorkflowReport
        result={result}
        variant="embedded"
        mode="interactive"
        firmName={firmName}
        onStartOver={startOver}
        onIdentify={requestEmail}
        onHighIntent={trackHighIntent}
      />
    );
  }

  return (
    <section
      id="workflow-finder"
      className="scroll-mt-24 grid gap-6 lg:grid-cols-[17rem_1fr] lg:items-start"
      data-ready={ready ? "true" : "false"}
    >
      <aside className="relative overflow-hidden rounded-3xl bg-text p-5 text-on-dark lg:sticky lg:top-24">
        <DarkGridBackdrop />
        <div className="relative z-10">
          <div className="flex items-center justify-between gap-4 lg:block">
            <div>
              <p className="font-heading text-xs font-black uppercase tracking-[0.18em] text-primary">
                Opportunity map
              </p>
              <p className="mt-2 font-heading text-lg font-black leading-snug">
                {firmName}
              </p>
            </div>
            <p className="text-xs font-bold tabular-nums text-on-dark/60 lg:mt-3">
              Step {visualStep} of {TOTAL_VISUAL_STEPS}
            </p>
          </div>
          <div
            className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-on-dark/15"
            aria-hidden="true"
          >
            <div
              className="h-full rounded-full bg-primary transition-all duration-300"
              style={{ width: progress }}
            />
          </div>
          <ol className="mt-7 hidden space-y-1 lg:block">
            {["Company profile", "Priorities", "Recurring work", "Systems"].map(
              (label, index) => {
                const itemStep = index + 1;
                const active = visualStep === itemStep;
                const complete = visualStep > itemStep;
                return (
                  <li
                    key={label}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-2 py-2 text-sm transition",
                      active
                        ? "bg-on-dark/10 font-bold text-on-dark"
                        : "text-on-dark/55",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-on-dark/25 font-heading text-xs font-black tabular-nums",
                        (active || complete) &&
                          "border-primary bg-primary text-text",
                      )}
                    >
                      {complete ? (
                        <Check size={13} strokeWidth={3} />
                      ) : (
                        itemStep
                      )}
                    </span>
                    {label}
                  </li>
                );
              },
            )}
          </ol>
          <div className="mt-7 hidden border-t border-on-dark/15 pt-5 text-xs leading-relaxed text-on-dark/55 lg:block">
            No client data. No readiness score. The complete plan appears before
            any contact request.
          </div>
        </div>
      </aside>

      <div className="rounded-3xl bg-surface-light px-5 py-7 shadow-sm sm:px-8 sm:py-9">
        {step === 1 && !firmConfirmed && (
          <div>
            <p className="font-heading text-xs font-black uppercase tracking-[0.18em] text-secondary-dark">
              Your firm
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-text sm:text-4xl">
              Which firm is this for?
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-text-secondary">
              Search the public Form ADV database so we can skip the boring
              questions. Not listed, or based outside the US? Just type the name
              and continue.
            </p>

            <div className="mt-6 border-t border-border pt-6">
              <FirmLookup
                firm={firm}
                manualName={manualName}
                organizationType={organizationType}
                country={country}
                aumRange={aumRange}
                teamSize={teamSize}
                correcting={correctingNumbers}
                onFirm={setFirm}
                onManualName={setManualName}
                onOrganizationType={setOrganizationType}
                onCountry={setCountry}
                onAumRange={setAumRange}
                onTeamSize={setTeamSize}
                onToggleCorrecting={setCorrectingNumbers}
              />
            </div>
          </div>
        )}

        {step === 1 && firmConfirmed && (
          <div>
            <p className="font-heading text-xs font-black uppercase tracking-[0.18em] text-secondary-dark">
              For {firmName}
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-text sm:text-4xl">
              What do you want to improve?
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-text-secondary">
              Choose everything that matters. We will connect those priorities
              to specific recurring work in the next step.
            </p>

            <div
              data-testid="advisor-workflow-finder-primary-outcome"
              className="mt-6 grid gap-3 sm:grid-cols-2"
            >
              {visibleGoals.map((option) => (
                <Choice
                  key={option.value}
                  selected={goals.includes(option.value)}
                  label={
                    organizationType === "other_industry"
                      ? option.label
                          .replace("advisors", "the team")
                          .replace("clients", "customers")
                      : option.label
                  }
                  detail={option.detail}
                  onClick={() => toggleGoal(option.value)}
                  testId={`goal-${option.value}`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="font-heading text-xs font-black uppercase tracking-[0.18em] text-secondary-dark">
              Linked to your priorities
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-text sm:text-4xl">
              Which recurring work is actually getting in the way?
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-text-secondary">
              These options come directly from what you selected. Choose any
              that are real for your team, then tell us how often each happens.
            </p>

            <div
              data-testid="advisor-workflow-finder-workflow-needs"
              className="mt-6 space-y-3"
            >
              {visibleWorkflows.map((value) => {
                const option = WORKFLOWS[value];
                const selected = workflows.includes(value);
                return (
                  <div
                    key={value}
                    className={cn(
                      "rounded-2xl bg-layer-1 p-4 shadow-sm ring-1 ring-border/70 transition",
                      selected && "bg-secondary/10 ring-2 ring-secondary",
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleWorkflow(value)}
                      data-testid={`workflow-${value}`}
                      className="flex min-h-12 w-full items-start gap-3 text-left focus-visible:outline-none"
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
                          selected
                            ? "bg-secondary text-on-dark"
                            : "bg-surface ring-1 ring-border",
                        )}
                      >
                        {selected && <Check size={14} strokeWidth={3} />}
                      </span>
                      <span>
                        <span className="block font-heading text-base font-bold text-text">
                          {option.label}
                        </span>
                        <span className="mt-1 block text-sm leading-snug text-text-secondary">
                          {option.detail}
                        </span>
                      </span>
                    </button>
                    {selected && (
                      <div className="mt-4 border-t border-border pt-4">
                        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-text-tertiary">
                          How often does this work repeat?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {FREQUENCIES.map((frequency) => (
                            <button
                              key={frequency.value}
                              type="button"
                              onClick={() =>
                                setFrequencies((current) => ({
                                  ...current,
                                  [value]: frequency.value,
                                }))
                              }
                              className={cn(
                                "min-h-10 rounded-lg px-3 text-xs font-semibold transition",
                                frequencies[value] === frequency.value
                                  ? "bg-secondary text-on-dark"
                                  : "bg-surface text-text-secondary hover:text-text",
                              )}
                            >
                              {frequency.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <p className="font-heading text-xs font-black uppercase tracking-[0.18em] text-secondary-dark">
              Your systems
            </p>
            <h2 className="mt-3 font-heading text-3xl font-black leading-tight text-text sm:text-4xl">
              Where does this work happen today?
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-text-secondary">
              Check every system involved. We use this to design the handoffs,
              not to recommend replacing your stack.
            </p>

            <div
              data-testid="advisor-workflow-finder-existing-systems"
              className="mt-6 space-y-6"
            >
              {groupedSystems.map(([group, options]) => (
                <div key={group}>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wider text-text-tertiary">
                    {group}
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {options.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={systems.includes(option.value)}
                        onClick={() => toggleSystem(option.value)}
                        data-testid={`system-${option.value}`}
                        className={cn(
                          "flex min-h-12 items-center gap-3 rounded-xl bg-layer-1 px-4 py-3 text-left text-sm font-semibold shadow-sm ring-1 ring-border/70 transition active:scale-[0.98]",
                          systems.includes(option.value)
                            ? "bg-secondary/10 text-text ring-2 ring-secondary"
                            : "text-text-secondary hover:-translate-y-0.5 hover:text-text hover:shadow-md",
                        )}
                      >
                        <span
                          className={cn(
                            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                            systems.includes(option.value)
                              ? "bg-secondary text-on-dark"
                              : "bg-surface ring-1 ring-border",
                          )}
                        >
                          {systems.includes(option.value) && (
                            <Check size={12} strokeWidth={3} />
                          )}
                        </span>
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-3 rounded-xl bg-primary/10 px-4 py-3 text-xs leading-relaxed text-text-secondary">
              <ShieldCheck
                size={17}
                className="mt-0.5 shrink-0 text-secondary-dark"
              />
              Do not enter client names, account numbers, documents, or message
              content. This tool only needs firm-level context.
            </div>
          </div>
        )}

        {error && (
          <p
            role="alert"
            className="mt-6 rounded-xl bg-error/10 px-4 py-3 text-sm text-error-dark"
          >
            {error}
          </p>
        )}

        <div className="mt-8 flex items-center gap-3 border-t border-border pt-6">
          {visualStep > 1 && (
            <HTFButton
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={loading}
            >
              <ArrowLeft size={16} />
              Back
            </HTFButton>
          )}
          <HTFButton
            type="button"
            variant="primary"
            data-testid={
              step === 1 && !firmConfirmed
                ? "advisor-workflow-finder-confirm-firm"
                : "advisor-workflow-finder-continue"
            }
            onClick={continueStep}
            disabled={!canContinue || loading}
          >
            {loading
              ? "Working…"
              : step === 3
                ? "Build my opportunity map"
                : "Continue"}
            {!loading && <ArrowRight size={16} />}
          </HTFButton>
        </div>
      </div>
    </section>
  );
}
